/* eslint-disable react-hooks/exhaustive-deps */
import { Flex, Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Card from "components/card/Card";
import AppSpinner from "components/spinner/AppSpinner";
import { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { ProjectTableProps, ProjectRowData, MOBILE_COLUMN_WIDTHS } from "./types";
import { createProjectColumns } from "./columnDefinitions";

export default function ProjectsTable({
  isFetching,
  tableData,
  onShowCustomerProjectDetails,
  tabIndex = 0,
}: ProjectTableProps) {
  const navigate = useNavigate();

  const [data, setData] = useState<ProjectRowData[]>([...tableData]);
  // Utility: Check if order status has changed
  const isStatusChanged = (orderId: number, statusId: string): string | null => {
    const prevStatus = localStorage.getItem("order_status")
      ? JSON.parse(localStorage.getItem("order_status")!)
      : [];

    if (!prevStatus.length) return null;

    const matchedItem = prevStatus.find(
      (it: any) => Number(it.order_id) === orderId
    );

    if (!matchedItem || Number(matchedItem?.order_status_id) === Number(statusId)) {
      return null;
    }
    return "Order status has been changed";
  };

  // Create columns with dependencies
  const columns = createProjectColumns({
    navigate,
    isStatusChanged,
    tabIndex,
  });

  // Update data when tableData changes
  useEffect(() => {
    setData([...tableData]);
  }, [tableData]);

  // Handle hash navigation
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const projectId = hash.replace("#", "");
      onShowCustomerProjectDetails(projectId);
    }
  }, []);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: false,
  });

  // Handler: Get row background color
  const getRowBg = (_row: ProjectRowData) => {
    return "transparent";
  };

  // Handler: Get row hover background
  const getRowHoverBg = (_row: ProjectRowData) => {
    return "#E8EDF2";
  };
  // Full-card loader for list view (как в grid)
  if (isFetching) {
    return (
      <Card
        flexDirection="column"
        w="100%"
        p="0px"
        overflowX="auto"
        borderRadius="16px"
        border="1px solid"
        borderColor="#E5E7EB"
      >
        <Flex justify="center" align="center" minH="400px" bg="#F5F7FA">
          <AppSpinner />
        </Flex>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      p="0px"
      overflowX="auto"
      borderRadius="16px"
      border="1px solid"
      borderColor="#E5E7EB"
    >
      <Table variant="unstyled" lineHeight="15px">
        <Thead bg="white">
          {table.getHeaderGroups().map((headerGroup, index) => (
            <Tr key={`header-${index}`}>
              {headerGroup.headers.map((header, headerIndex) => (
                <Th
                  key={`header-col-${headerIndex}`}
                  colSpan={header.colSpan}
                  px="16px"
                  py="16px"
                  border="none"
                  borderBottom="none"
                  fontSize="14px"
                  textTransform="none"
                  letterSpacing="normal"
                  width={
                    header.column.id === "languages"
                      ? { base: "auto", md: "auto", lg: "220px" }
                      : "auto"
                  }
                  maxW={
                    header.column.id === "languages"
                      ? { base: "auto", md: "auto", lg: "220px" }
                      : "auto"
                  }
                  bg="white"
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>

        <Tbody bg="#F5F7FA">
          {table.getRowModel().rows.map((row, rowIndex) => (
            <Fragment key={`row-${rowIndex}`}>
              <Tr
                borderBottom="1px solid"
                borderColor="#E5E7EB"
                transition="all 0.2s"
                height="85px"
                bg={getRowBg(row.original)}
                _hover={{ bg: getRowHoverBg(row.original) }}
              >
                {row.getVisibleCells().map((cell, cellIndex) => (
                  <Td
                    key={`row-${rowIndex}-cell-${cellIndex}`}
                    padding={{ md: "16px", base: "16px" }}
                    verticalAlign="middle"
                    fontSize="16px"
                    minW={{
                      base: `${MOBILE_COLUMN_WIDTHS[cell.column.id as keyof typeof MOBILE_COLUMN_WIDTHS]}px`,
                      md: `${MOBILE_COLUMN_WIDTHS[cell.column.id as keyof typeof MOBILE_COLUMN_WIDTHS]}px`,
                      lg: "auto",
                    }}
                    border="none !important"
                    width={
                      cell.column.id === "actionCol"
                        ? "200px"
                        : cell.column.id === "languages"
                          ? { base: "auto", md: "auto", lg: "220px" }
                          : "auto"
                    }
                    maxW={
                      cell.column.id === "languages"
                        ? { base: "auto", md: "auto", lg: "220px" }
                        : "auto"
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            </Fragment>
          ))}
        </Tbody>
      </Table>
    </Card>
  );
}

